import { createAsyncThunk } from '@reduxjs/toolkit';
import { setMemberData } from '../reducers/member';
import { fetchFavouriteOrders } from './fetchFavouriteOrders';
import { fetchLocationOffers } from './fetchLocationOffers';

/**
 * # Sets up member for kiosk usage
 *
 * This thunk is similar to `setMember` but tailored for kiosk usage
 *
 * - Sets member data
 * - Fetches location offers,
 * - And fetches favourite orders with 'trusted' authentication
 * - These should not be done simultaneously because the signing process requires sequential execution.
 *
 * @param data.member The member to set (already processed)
 *
 * @returns The member that was set
 */
export const setMemberKiosk = createAsyncThunk(
  '$setMemberKiosk',
  async (
    data: {
      member?: Member;
    },
    { dispatch, rejectWithValue },
  ) => {
    const { member } = data;

    try {
      if (!member) {
        throw new Error('no member data supplied');
      }

      dispatch(setMemberData(member));

      /**
       * ! Note:
       * For trusted API calls, they need to be done one at a time to avoid
       * conflicts where calling them at the same time will trigger a new
       * signature creation in the shell invalidating the other that would
       * be used for particular request.
       * -> See: https://app.clickup.com/t/86d0mrrk1?comment=90160141608263
       *
       * ! Note 2:
       * You should never make any fetches in here that would take way too
       * long to load and don't require the user to see them immediately
       * (like past member orders etc). These should be fetched later on
       * demand, while showing a skeleton loader in the UI.
       */

      await dispatch(fetchLocationOffers());

      await dispatch(
        fetchFavouriteOrders({
          authenticationMethod: 'trusted',
        }),
      );

      return member;
    } catch (e) {
      console.error('Set member failed', e);
      return rejectWithValue(e);
    }
  },
);
